<template>
  <stripe-bridge-dialog :open="open" :title="product ? 'Metadata of ' + product.name : 'Metadata'" :busy="busy" @close="$emit('cancel')">
    <p v-if="mode">
      <aha-status-badge size="sm" :look="mode === 'live' ? 'alert' : 'quiet'">{{ mode === 'live' ? 'LIVE' : 'TEST' }} mode</aha-status-badge>
    </p>
    <p class="bridge-muted">Keys you remove are unset in Stripe. Values are saved as text.</p>
    <p v-if="!rows.length" class="bridge-muted">This product has no metadata.</p>
    <div v-for="(row, index) in rows" :key="row.id" class="metadata-row">
      <aha-input
        aria-label="Key"
        placeholder="Key"
        :value="row.key"
        :disabled.prop="busy"
        @input="row.key = detailValue($event, row.key)"
      ></aha-input>
      <aha-input
        aria-label="Value"
        placeholder="Value"
        :value="row.value"
        :disabled.prop="busy"
        @input="row.value = detailValue($event, row.value)"
      ></aha-input>
      <aha-button icon-only size="xs" variant="tertiary" aria-label="Remove row" :disabled="busy" @click="rows.splice(index, 1)">
        <aha-icon name="system-x" size="14" decorative></aha-icon>
      </aha-button>
    </div>
    <div>
      <aha-button size="sm" variant="secondary" :disabled="busy" @click="addRow">Add key</aha-button>
    </div>
    <aha-alert v-if="validationError" type="warning" size="small">{{ validationError }}</aha-alert>
    <aha-alert v-if="error" type="error" size="small">{{ error }}</aha-alert>
    <template slot="footer">
      <aha-button variant="secondary" :disabled="busy" @click="$emit('cancel')">Cancel</aha-button>
      <aha-button :disabled="busy || !!validationError || !changed" :loading="busy" @click="save">Save metadata</aha-button>
    </template>
  </stripe-bridge-dialog>
</template>

<script>
import { detailValue } from './catalogMixin'
import nativeAhaElements from './nativeAhaElements'
import StripeBridgeDialog from './StripeBridgeDialog'

let rowCounter = 0

export default {
  name: 'StripeBridgeMetadataEditor',
  components: { ...nativeAhaElements, StripeBridgeDialog },
  props: {
    open: { type: Boolean, default: false },
    mode: { type: String, default: '' },
    product: { type: Object, default: null },
    busy: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  data() {
    return { rows: [] }
  },
  computed: {
    original() {
      return (this.product && this.product.metadata) || {}
    },
    keptRows() {
      return this.rows.filter(row => row.key.trim() || row.value.trim())
    },
    validationError() {
      const keys = this.keptRows.map(row => row.key.trim())
      if (keys.some(key => !key)) return 'Every value needs a key.'
      if (this.keptRows.some(row => !row.value.trim())) return 'Every key needs a value. Remove the row to unset a key.'
      if (new Set(keys).size !== keys.length) return 'Each key can only appear once.'
      if (keys.some(key => key.length > 40)) return 'Keys can be at most 40 characters.'
      return ''
    },
    payload() {
      const next = Object.fromEntries(this.keptRows.map(row => [row.key.trim(), row.value]))
      const removed = Object.keys(this.original).filter(key => !(key in next))
      return { ...Object.fromEntries(removed.map(key => [key, ''])), ...next }
    },
    changed() {
      return Object.entries(this.payload).some(([key, value]) => (this.original[key] === undefined ? '' : this.original[key]) !== value)
    },
  },
  watch: {
    open(value) {
      if (value) this.rows = Object.entries(this.original).map(([key, value]) => this.newRow(key, String(value)))
    },
  },
  methods: {
    newRow(key, value) {
      rowCounter += 1
      return { id: rowCounter, key, value }
    },
    addRow() {
      this.rows.push(this.newRow('', ''))
    },
    detailValue,
    save() {
      if (this.validationError || !this.changed) return
      this.$emit('save', this.payload)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './bridgeStyles.scss';

.metadata-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: var(--aha-space-8);
  align-items: center;
}
</style>
